/**
 * Affiliate API.
 *
 * An affiliate is NOT an investor. They risk no capital, hold no shares, and are owed a
 * flat fee per attributed ticket. Nothing here touches the investor or offering types.
 *
 * The dashboard read is UNAUTHENTICATED: an affiliate has no Voleto account, and the
 * contract number is the entire credential. The engine rate limits that endpoint, and it
 * is a POST so the credential never lands in a server log, a Referer header or a browser
 * history entry the way a query string would.
 */

import { api } from './apiClient';

export type ThresholdScope = 'lifetime' | 'per_event';

export interface AffiliateStats {
  clicks: number;
  tickets: number;
  /** Tickets past the threshold. These are the ones that actually earn. */
  commissionableTickets: number;
  commissionCents: number;
}

export interface AffiliateDashboard {
  contractNumber: string;
  affiliate: {
    title: string;
    code: string;
    /**
     * Computed by the engine, never typed. Ready to copy, and it routes through the
     * engine's redirect so the click is recorded and the attribution cookie is set.
     */
    trackingLink: string | null;
    /** Where that link lands. For display only: sharing it skips the attribution. */
    destinationUrl: string | null;
    commissionPerTicketCents: number;
    /** Exclusive: 25 means the 26th attributed ticket is the first that earns. */
    commissionStartsAfter: number;
    thresholdScope: ThresholdScope;
  };
  event: {
    id: string;
    title: string;
    date: string | null;
    venueName: string | null;
    venueCity: string | null;
  };
  /**
   * Independent per affiliate. The poster travels with the video because autoplay is
   * refused often enough that a dashboard must never be configured with motion and no
   * fallback.
   */
  background: { videoUrl: string | null; posterUrl: string | null };
  /** Figures at whatever scope this contract is set to. */
  stats: AffiliateStats;
  /** Always this event's own figures, whatever the scope. */
  thisEvent: AffiliateStats & { eventId: string };
  startDate: string;
}

/** Unlock with a contract number. No authentication; the number is the credential. */
export const unlockAffiliateDashboard = (contractNumber: string) =>
  api.post<AffiliateDashboard>('/affiliates/unlock', { contractNumber });

// ── Admin surface ───────────────────────────────────────────────────────────
//
// Everything below is behind requireAdmin in the engine. An affiliate is a negotiated
// commercial arrangement, so the terms are set here and never self-serve.

export interface AdminAffiliate {
  id: string;
  title: string;
  /** In every link already handed out, so the engine refuses to change it. */
  code: string;
  baseUrl: string;
  /** Computed by the engine from baseUrl + code. Never typed, never stored. */
  trackingLink: string | null;
  commissionPerTicketCents: number;
  /** Exclusive: 25 means the 26th attributed ticket is the first that earns. 0 means all. */
  commissionStartsAfter: number;
  thresholdScope: ThresholdScope;
  backgroundVideoUrl: string | null;
  backgroundVideoPosterUrl: string | null;
  status: 'active' | 'inactive';
  createdAt: string;
  contractCount: number;
}

export interface AffiliateContract {
  id: string;
  /** The credential that unlocks the affiliate's dashboard. Hand this to them. */
  contractNumber: string;
  createdAt: string;
  event: { id: string; title: string; date: string | null; status: string };
}

export interface AffiliateStatsResponse {
  affiliate: AdminAffiliate;
  totals: AffiliateStats;
  perEvent: (AffiliateStats & { eventId: string })[];
}

export interface AffiliateInput {
  title: string;
  baseUrl: string;
  commissionPerTicketCents: number;
  commissionStartsAfter: number;
  thresholdScope: ThresholdScope;
  backgroundVideoUrl?: string | null;
  backgroundVideoPosterUrl?: string | null;
  /** Derived from the title when omitted. Immutable once issued. */
  code?: string;
}

export const listAffiliates = () => api.get<AdminAffiliate[]>('/admin/affiliates');

export const createAffiliate = (input: AffiliateInput) =>
  api.post<AdminAffiliate>('/admin/affiliates', input);

/** The tracking code is deliberately absent: the engine refuses to change it. */
export const updateAffiliate = (
  id: string,
  input: Partial<Omit<AffiliateInput, 'code'>> & { status?: 'active' | 'inactive' },
) => api.patch<AdminAffiliate>(`/admin/affiliates/${id}`, input);

export const listAffiliateContracts = (id: string) =>
  api.get<AffiliateContract[]>(`/admin/affiliates/${id}/contracts`);

export const createAffiliateContract = (id: string, eventId: string) =>
  api.post<{ id: string; affiliateId: string; eventId: string; contractNumber: string }>(
    `/admin/affiliates/${id}/contracts`, { eventId });

export const getAffiliateStats = (id: string) =>
  api.get<AffiliateStatsResponse>(`/admin/affiliates/${id}/stats`);
